import Script from 'next/script';
import type { TranscendIndividualTraits } from '@transcend-io/segment/core';
import { z } from 'zod';

import { useConsentManager } from './consent-manager';
import { identifyWithoutId } from './segment';

interface DnbResponseRaw {
  status: string;
  customerDataPersonalization: Record<string, string>;
  [key: string]: string | 'NULL' | null | Record<string, string>;
}

/** String-like response from D&B */
const String = z
  .nullish(z.string())
  // Transform empty or nullish values to undefined
  .transform((val) =>
    ['NULL', '', null, undefined].includes(val) ? undefined : (val as string),
  );

/** Number-like response from D&B */
const Integer = String.transform((val) => {
  if (val === undefined) return undefined;
  const number = parseInt(val as string);
  if (!Number.isFinite(number) || Number.isNaN(number)) {
    const error = new TypeError(`Invalid integer in DnB response: ${val}`);
    console.error(error);
    return undefined;
  }
  return number;
});

/** Post-processed schema for D&B response */
const DnbResponseTransformed = z.looseObject({
  status: String,
  message: String,
  duns: Integer,
  parentDuns: Integer,
  domesticUltimateDuns: Integer,
  ultimateDuns: Integer,
  companyName: String,
  companyAddress: String,
  companyCity: String,
  companyState: String,
  companyZip5: String,
  companyZip4: String,
  companyCounty: String,
  companyMsa: String,
  companyRegion: String,
  companyCountry: String,
  fortune1000: String,
  industryNaics: String,
  naicsCodes: String,
  industrySic: String,
  sicCodes: String,
  salesAnnual: String,
  salesAnnualNum: Integer,
  employeesAtLocation: String,
  employeesAtLocationNum: Integer,
  employeesInAllLocations: String,
  employeesInAllLocationsNum: Integer,
  familyTreeSize: String,
  familyTreeSizeNum: Integer,
  networth: String,
  networthNum: Integer,
  walletSize: String,
  cardOfferResponsiveness: String,
  propensityForLoan: String,
  propensityForLease: String,
  propensityForLoc: String,
  itExpense: String,
  telecomSpend: String,
  techOfficeDemand: String,
  delinquencyRate: String,
  howTheyPay: String,
  marketability: String,
  tradeStyle: String,
  companyTelephone: String,
  domain: String,
  parentName: String,
  domesticUltimateName: String,
  ultimateName: String,
  ticker: String,
  exchange: String,
  ein: String,
  customerDataPersonalization: z.nullish(z.record(z.string(), z.unknown())),
  id: String,
  ipCountry: String,
});

type DnbResponseTransformed = z.infer<typeof DnbResponseTransformed>;

interface DnbVid {
  getData: (
    apiKey: string,
    format: string,
    type: string,
    params: Record<string, unknown>,
    callback: (data: DnbResponseRaw) => void,
  ) => void;
}

declare global {
  interface Window {
    /** D&B (Dun & Bradstreet) Visitor Intelligence API */
    dnbvid?: DnbVid;
  }
}

/**
 * Load the D&B (Dun & Bradstreet) Visitor Intelligence scripts after airgap.js is initialized
 */
export const DnbPixelScript: React.FC = () => {
  const { airgap } = useConsentManager();

  const loadScript = airgap || process.env.VERCEL_ENV !== 'production';
  return (
    <>
      {loadScript && (
        <>
          {/* For opt-out information on Visitor Intelligence, please visit: https://d41.co/ */}
          <Script
            id="dnb-sync"
            src="//paapi2959.d41.co/sync/"
            strategy="lazyOnload"
            async
            defer
          />
          {/* D&B core tag script */}
          <Script
            id="dnb-coretag"
            src="//cdn-0.d41.co/tags/dnb_coretag_v6.min.js"
            strategy="lazyOnload"
            onLoad={() => {
              if (typeof window !== 'undefined' && window.dnbvid) {
                window.dnbvid.getData(
                  'paapi2959',
                  'json',
                  'T',
                  {},
                  function (dnbResponseRaw: DnbResponseRaw) {
                    if (dnbResponseRaw.status == '200') {
                      const transformed =
                        DnbResponseTransformed.parse(dnbResponseRaw);

                      // Map the D&B response into Segment tracking plan's traits
                      const dnbTraits: TranscendIndividualTraits = {
                        company: {
                          name: transformed.companyName,
                          employee_count:
                            transformed.employeesInAllLocationsNum,
                          industry: transformed.industryNaics,

                          duns: transformed.duns,
                          parentName: transformed.parentName,
                          parentDuns: transformed.parentDuns,
                          ultimateName: transformed.ultimateName,
                          ultimateDuns: transformed.ultimateDuns,
                          address: {
                            street: transformed.companyAddress,
                            city: transformed.companyCity,
                            state: transformed.companyState,
                            postalCode: transformed.companyZip5,
                            country: transformed.companyCountry,
                          },
                          dnb: transformed,
                        },
                      };

                      // Update the user traits with the D&B response
                      identifyWithoutId(dnbTraits);
                    }
                  },
                );
              }
            }}
          />
        </>
      )}
    </>
  );
};
